import React, { useState, useEffect, useRef } from 'react';
import { Chart } from 'chart.js/auto';
import { ConstructorEventUpdateSQLStatus } from '../../../../Event/event';

const DailyActivityQuiz = () => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const initialState = {
    sleep: 8,
    school: 6,
    tv: 4,
    play: 3,
    other: 3,
  };

  const [sliders, setSliders] = useState(initialState);
  const [remaining, setRemaining] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [lastIndex, setLastIndex] = useState(-1);
  const [scenarioText, setScenarioText] = useState('Loading question...');

  const questions = [
    {
      text: "Sam is tired and wants more sleep. Help him get 10 hours of sleep and cut TV to 2 hours.",
      answer: { sleep: 10, school: 6, tv: 2 }
    },
    {
      text: "Today is a school day. Sam needs 8 hours of school and no more than 1 hour of TV.",
      answer: { school: 8, tv: 1 }
    },
    {
      text: "It's Saturday! Sam wants 5 hours of play and no school.",
      answer: { play: 5, school: 0 }
    },
    {
      text: "Sam has a cold. Help him get 12 hours of sleep and reduce other activities.",
      answer: { sleep: 12 }
    },
    {
      text: "It's screen-free day! Set Sam's TV time to 0.",
      answer: { tv: 0 }
    }
  ];

  useEffect(() => {
    if (chartRef.current && !chartInstanceRef.current) {
      chartInstanceRef.current = new Chart(chartRef.current, {
        type: 'pie',
        data: {
          labels: ['Sleep', 'School', 'TV', 'Play', 'Other'],
          datasets: [{
            data: Object.values(sliders),
            backgroundColor: ['#6A5ACD', '#FFA500', '#FF4500', '#32CD32', '#00CED1']
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { position: 'bottom' }
          }
        }
      });
    } else if (chartInstanceRef.current) {
      chartInstanceRef.current.data.datasets[0].data = Object.values(sliders);
      chartInstanceRef.current.update();
    }
    updateRemainingTime();
  }, [sliders]);

  const updateRemainingTime = () => {
    const total = Object.values(sliders).reduce((a, b) => a + b, 0);
    setRemaining(24 - total);
  };

  const updateSlider = (key, value) => {
    setSliders(prev => ({ ...prev, [key]: parseInt(value) }));
    setFeedback('');
  };

  const checkAnswer = () => {
    const required = questions[currentQuestionIndex].answer;
    const isCorrect = Object.entries(required).every(([key, val]) => sliders[key] === val);

    if (isCorrect) {
      setFeedback('✅ Great job! You matched Sam\'s needs.');
         const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
    } else {
      setFeedback('❌ Hmm, that\'s not quite right. Try adjusting again!');
    }
  };

  const loadQuestion = () => {
    let nextIndex;
    do {
      nextIndex = Math.floor(Math.random() * questions.length);
    } while (nextIndex === lastIndex);

    setCurrentQuestionIndex(nextIndex);
    setLastIndex(currentQuestionIndex);
    setScenarioText(questions[nextIndex].text);
    setFeedback('');
    setSliders(initialState);
  };

  useEffect(() => {
    loadQuestion();
  }, []);

  const labelStyle = {
    marginBottom: 5,
    display: 'block'
  };

  const inputGroupStyle = {
    marginBottom: 15,
    width: '100%'
  };

  const styles = {
    container: {
      fontFamily: 'Comic Sans MS, cursive, sans-serif',
      backgroundColor: '#07EB94',
      padding: 20,
      textAlign: 'center',
      maxWidth: 800,
      margin: '0 auto'
    },
    avatar: {
      fontSize: 60,
      animation: 'bounce 2s infinite',
      marginTop: 10
    },
    scenario: {
      fontSize: '1.2em',
      backgroundColor: '#fff0c4',
      borderRadius: 12,
      padding: 15,
      border: '2px solid #ffd700',
      margin: '20px auto'
    },
    chartContainer: {
      maxWidth: 400,
      margin: '0 auto'
    },
    inputWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: 20,
      width: '100%'
    },
    remainingTime: {
      fontSize: '1.2em',
      marginTop: 10,
      color: remaining < 0 ? 'red' : 'green'
    },
    button: {
      margin: 10,
      padding: '10px 20px',
      fontSize: 16,
      borderRadius: 10,
      border: 'none',
      cursor: 'pointer'
    },
    checkBtn: {
      backgroundColor: '#4CAF50',
      color: 'white'
    },
    nextBtn: {
      backgroundColor: '#2196F3',
      color: 'white'
    },
    feedback: {
      fontWeight: 'bold',
      marginTop: 10,
      color: feedback.includes('Great') ? 'green' : 'red'
    }
  };

  return (
    <div style={styles.container}>
      <h3>Help Sam Plan His Day!</h3>
      <div style={styles.avatar}>🧒💭</div>
      <div style={styles.scenario}>{scenarioText}</div>

      <div style={styles.chartContainer}>
        <canvas ref={chartRef}></canvas>
      </div>

      <div style={styles.inputWrapper}>
        {['sleep', 'school', 'tv', 'play', 'other'].map(key => (
          <div key={key} style={inputGroupStyle}>
            <label style={labelStyle}>
              {key.charAt(0).toUpperCase() + key.slice(1)}: <strong>{sliders[key]} hrs</strong>
            </label>
            <input
              type="range"
              min="0"
              max="24"
              value={sliders[key]}
              onChange={(e) => updateSlider(key, e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        ))}

        <div style={styles.remainingTime}>
          {remaining < 0
            ? `You've used ${-remaining} extra hour(s)!`
            : `You have ${remaining} hour${remaining !== 1 ? 's' : ''} left in your day!`}
        </div>

        <div>
          <button style={{ ...styles.button, ...styles.checkBtn }} onClick={checkAnswer}>Check Answer</button>
          <button style={{ ...styles.button, ...styles.nextBtn }} onClick={loadQuestion}>Next Question</button>
        </div>

        <div style={styles.feedback}>{feedback}</div>
      </div>
    </div>
  );
};

export default DailyActivityQuiz;
